import {SECTIONS, tagVideo} from "./taxonomy";

// A topic only surfaces as its own sub-playlist when it has at least this many
// videos; smaller topics roll up into the section's flat "More" list. (Per the
// product rule in CATALOG-API-PLAYLISTS-ASK.md — the data tags every video
// regardless; the threshold is a UI concern.)
export const PLAYLIST_THRESHOLD = 3;

// Group a flat video list into Section -> Playlist -> ordered videos.
//
// Returns:
//   {
//     intro:      [video],            // is_intro, pinned above everything
//     sections:   [{                  // only sections that have content
//       id, title, icon, order,
//       playlists: [{id, title, videos: [video]}],  // >= threshold, title-sorted
//       looseVideos: [video],         // topics under threshold, flattened
//     }],
//     untagged:   [video],            // no section/playlist resolved
//   }
export const buildCatalog = (videos, {threshold = PLAYLIST_THRESHOLD} = {}) => {
  const intro = [];
  const untagged = [];
  // section_id -> {playlists: Map<playlist_id, {id, title, items:[{video, order}]}>}
  const bySection = new Map();

  for (const video of videos || []) {
    const tag = tagVideo(video);

    if (tag.is_intro) {
      intro.push(video);
      continue;
    }
    if (!tag.section_id || !tag.playlist_id) {
      untagged.push(video);
      continue;
    }

    if (!bySection.has(tag.section_id)) {
      bySection.set(tag.section_id, {playlists: new Map()});
    }
    const section = bySection.get(tag.section_id);
    if (!section.playlists.has(tag.playlist_id)) {
      section.playlists.set(tag.playlist_id, {
        id: tag.playlist_id,
        title: tag.playlist_title || tag.playlist_id,
        items: [],
      });
    }
    section.playlists.get(tag.playlist_id).items.push({
      video,
      order: Number.isFinite(Number(tag.order_in_playlist))
        ? Number(tag.order_in_playlist)
        : null,
    });
  }

  const sections = SECTIONS.filter((s) => bySection.has(s.id))
    .map((s) => {
      const raw = bySection.get(s.id);
      const playlists = [];
      const looseVideos = [];

      for (const p of raw.playlists.values()) {
        // Order videos by order_in_playlist; null orders keep their incoming
        // order at the end (stable sort).
        const ordered = p.items
          .map((it, i) => ({...it, i}))
          .sort((a, b) => {
            if (a.order == null && b.order == null) return a.i - b.i;
            if (a.order == null) return 1;
            if (b.order == null) return -1;
            return a.order - b.order;
          })
          .map((it) => it.video);

        // Demo mode: trades-first order for the playlist holding the pinned
        // construction video (id 878). Pin first, then building trades /
        // industrial / transport / outdoor work, then everything else in its
        // original order — so the player's "Up next" recommends manual-labor
        // careers after the construction video instead of the computer ones.
        if (process.env.REACT_APP_DEMO_ACCOUNT_PREFIX) {
          const DEMO_ORDER = [
            "878", // Join the Construction Industry (the pinned demo video)
            "880", // Construction Laborer
            "886", // Construction Pre-Apprenticeship Program
            "879", // Carpenter Apprentice
            "881", // Electrician
            "882", // Electrician Apprenticeship Program
            "883", // Sheet Metal Worker
            "884", // Plumbers and Pipefitters
            "885", // What is a Pipefitter?
            "887", // What is a Cement Mason
            "888", // Ironworkers What We Do
            "889", // Heavy Equipment Operator School
            "904", // Welder 360°
            "902", // High Demand Jobs: Machining and Welding
            "903", // Machinist 360°
            "900", // CNC Operator 360°
            "901", // Industrial Mechanic 360°
            "899", // Manufacturing Overview 360
            "908", // Autovol
            "907", // Johnson Thermal
            "905", // Western Trailers
            "906", // GoGo squeeZ
            "918", // Hand Laborers and Freight/Stock/Material Movers
            "916", // How to Get Started in Transportation
            "919", // Transportation Distribution and Logistics Career
            "920", // Truck Drivers
            "922", // Amoth Trucking 360
            "921", // Locomotive Engineers
            "944", // Mining in Idaho
            "945", // Mahon Logging
            "946", // Forestry
            "917", // Working in Aviation
          ];
          const rank = new Map(DEMO_ORDER.map((id, i) => [id, i]));
          if (ordered.some((v) => rank.get(String(v.id)) === 0)) {
            const keyed = ordered.map((v, i) => ({
              v,
              r: rank.has(String(v.id))
                ? rank.get(String(v.id))
                : DEMO_ORDER.length + i,
            }));
            keyed.sort((a, b) => a.r - b.r);
            ordered.splice(0, ordered.length, ...keyed.map((k) => k.v));
          }
        }

        if (ordered.length >= threshold) {
          playlists.push({id: p.id, title: p.title, videos: ordered});
        } else {
          looseVideos.push(...ordered);
        }
      }

      playlists.sort((a, b) => a.title.localeCompare(b.title));
      return {...s, playlists, looseVideos};
    })
    // A section with no qualifying playlists AND no loose videos shouldn't show.
    .filter((s) => s.playlists.length > 0 || s.looseVideos.length > 0);

  return {intro, sections, untagged};
};
